import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        kind: "contact",
        name: form.name,
        email: form.email,
        message: form.message,
      });
      setSent(true);
      toast.success("Message sent — thank you for reaching out.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "bg-white border-2 border-border rounded-xl px-4 py-4 h-auto text-lg focus:ring-2 focus:ring-sage focus:border-transparent outline-none transition-all";

  return (
    <div data-testid="contact-page" className="bg-cream">
      <Seo
        title="Contact UNSTUCK by Tammi"
        description="Questions about UNSTUCK Seniors activity decks, orders, or bulk pricing for your senior community? Contact UNSTUCK by Tammi."
        path="/contact"
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">Contact</p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-ink mb-6">
              We would love to hear from you.
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-8">
              Questions about UNSTUCK Seniors, an order, or one of the other collections? Send us a
              message and we will get back to you.
            </p>
            <div className="flex items-start gap-4 bg-white rounded-2xl border border-border p-6">
              <div className="h-12 w-12 shrink-0 rounded-full bg-sage-light flex items-center justify-center">
                <Mail className="h-6 w-6 text-sage" aria-hidden="true" />
              </div>
              <div>
                <p className="font-heading font-bold text-lg text-ink mb-1">Prefer email?</p>
                <p className="text-base text-ink/70">
                  Write to us anytime at{" "}
                  <a
                    href="mailto:Unstuckbytammi@gmail.com"
                    data-testid="contact-email-link"
                    className="text-sage font-semibold hover:text-sage-dark underline underline-offset-4"
                  >
                    Unstuckbytammi@gmail.com
                  </a>{" "}
                  — or use the form and it lands in the same inbox.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8 sm:p-10">
            {sent ? (
              <div data-testid="contact-form-success" className="text-center py-10">
                <div className="h-16 w-16 rounded-full bg-sage-light flex items-center justify-center mx-auto mb-6">
                  <Check className="h-8 w-8 text-sage" aria-hidden="true" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-ink mb-3">Message sent</h2>
                <p className="text-lg text-ink/75">Thank you for reaching out — we will reply as soon as we can.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div>
                  <Label htmlFor="contact-name" className="text-base font-medium mb-1.5 block">Your name *</Label>
                  <Input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={update("name")} className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="contact-email" className="text-base font-medium mb-1.5 block">Email *</Label>
                  <Input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={update("email")} className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="contact-message" className="text-base font-medium mb-1.5 block">Message *</Label>
                  <Textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={update("message")} className={inputCls} />
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="w-full bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 h-auto text-lg font-medium transition-all duration-300"
                >
                  {sending && <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />}
                  Send Message
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
